import { FC, useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Button } from '@smartshell/ui-kit';

import { LocalizationContext } from 'localization';
import { GOODS } from 'schemas';
import { printWorkShiftReport } from 'services/printWorkShiftReport';
import { formatFIO } from 'services/printWorkShiftReport/utils';
import { SettingsContext } from 'settings';
import { TGood } from 'types';
import { usePrintContext } from 'utils';

import {
  IGetWorkShiftDataResponse,
  IPrintWorkShiftReportProps,
} from '../interfaces';
import { GET_WORKSHIFT_DATA } from '../schema';

export const PrintWorkShiftReport: FC<IPrintWorkShiftReportProps> = ({
  comment,
  onAfterPrint,
  disabled = false,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency, clubName, printReportAllGoods },
    },
  } = useContext(SettingsContext);
  const { startPrint, endPrint } = usePrintContext();

  const [isGenerating, setIsGenerating] = useState(false);

  const { data: paymentsResponse, loading } =
    useQuery<IGetWorkShiftDataResponse>(GET_WORKSHIFT_DATA, {
      fetchPolicy: 'cache-only',
    });

  const { data } = useQuery<{ goods: Array<TGood> }>(GOODS, {
    variables: {
      input: {
        title_search: undefined,
        category_id: undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !printReportAllGoods,
    notifyOnNetworkStatusChange: true,
  });

  const allGoods = data?.goods ?? [];

  if (paymentsResponse) {
    const {
      id,
      cash_orders,
      cash_on_start,
      cash,
      card,
      deposit,
      online_deposit,
      created_at,
      bonus,
      worker,
      goods,
      combos,
      services,
    } = paymentsResponse.getWorkShiftPaymentOverviewData;

    const cashOrdersDiff = cash_orders.reduce((acc, order) => {
      if (order.type === 'PKO') {
        return acc + order.sum;
      }

      if (order.type === 'RKO') {
        return acc - order.sum;
      }

      return acc;
    }, 0);

    const inCashBox = cash_on_start + cash + cashOrdersDiff;

    const goodsSorted = useMemo(
      () => [...(goods || [])].sort((a, b) => a.title.localeCompare(b.title)),
      [goods]
    );

    const allGoodsSorted = useMemo(
      () =>
        allGoods
          .filter(({ title }) => !goods?.some((good) => good.title === title))
          .sort((a, b) => a.title.localeCompare(b.title)),
      [allGoods, goods]
    );

    return (
      <Button
        variant="secondary"
        fullWidth
        onClick={async () => {
          startPrint();
          try {
            setIsGenerating(true);

            await printWorkShiftReport({
              clubName,
              goods: goodsSorted,
              services,
              cashOrders: cash_orders,
              id,
              bonus,
              worker: formatFIO(worker),
              card,
              cash,
              cash_on_start,
              combos,
              comment,
              currency: currency.sign,
              deposit,
              online_deposit,
              starts: created_at,
              t,
              inCashBox,
              printReportAllGoods,
              allGoods: allGoodsSorted,
            });

            endPrint();
            onAfterPrint?.();
          } catch (e) {
            console.error(e);
          } finally {
            setIsGenerating(false);
          }
        }}
        disabled={disabled || isGenerating}
        isLoading={isGenerating || loading}
      >
        {t('reports.workShift.print.print')}
      </Button>
    );
  }

  return null;
};
